import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Activity, Bike, Flame, Dumbbell, Waves, Zap, Check, Footprints } from 'lucide-react';
import WorkoutModal from './WorkoutModal';

const DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
const MONTHS_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// --- Componente de Icono para Ciclo Indoor / Rodillo ---
export const IndoorBikeIcon = ({ size = 18, strokeWidth = 2.5, color = '#ffffff', ...props }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth={strokeWidth} 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    {...props}
  >
    {/* Base horizontal estable de bici estática / rodillo */}
    <path d="M3 20h18" />
    {/* Volante de inercia frontal */}
    <circle cx="16.5" cy="14.5" r="3.5" />
    {/* Estructura del cuadro */}
    <path d="M6 20l4.5-9h4" />
    {/* Sillín y tija */}
    <path d="M5.5 10h4" />
    <path d="M7.5 10v7" />
    {/* Manillar de spinning / aero indoor */}
    <path d="M15 11l1.5-4h3" />
    {/* Biela y pedal */}
    <path d="M11 15.5l2 2" />
  </svg>
);

// --- Helpers para Detección de Deporte e Intensidad ---

export const isIndoorActivity = (itemOrType) => {
  if (!itemOrType) return false;
  if (typeof itemOrType === 'string') {
    return ['VirtualRide', 'IndoorCycling'].includes(itemOrType);
  }
  const t = itemOrType.type || '';
  const st = itemOrType.sport_type || itemOrType.sportType || '';
  const name = (itemOrType.name || '').toLowerCase();
  
  if (t === 'VirtualRide' || st === 'VirtualRide' || t === 'IndoorCycling' || st === 'IndoorCycling') {
    return true;
  }
  if (itemOrType.trainer === true || itemOrType.indoor === true) {
    return true;
  }
  if (name.includes('rodillo') || name.includes('indoor') || name.includes('zwift') || 
      name.includes('bkool') || name.includes('rouvy') || name.includes('ciclo indoor') || 
      name.includes('cicloindoor') || name.includes('spinning') || name.includes('trainer')) {
    return true;
  }
  return false;
};

export const isCyclingActivity = (itemOrType) => {
  if (!itemOrType) return false;
  if (typeof itemOrType === 'string') {
    return ['Ride', 'VirtualRide', 'GravelRide', 'EBikeRide', 'MountainBikeRide', 'IndoorCycling'].includes(itemOrType);
  }
  const t = itemOrType.type || '';
  const st = itemOrType.sport_type || itemOrType.sportType || '';
  const CYCLING = ['Ride', 'VirtualRide', 'GravelRide', 'EBikeRide', 'MountainBikeRide', 'IndoorCycling'];
  return CYCLING.includes(t) || CYCLING.includes(st) || /ciclismo|bici|ride|rodillo/i.test(itemOrType.name || '');
};

const getSportBadge = (itemOrType, isPlanned) => {
  const iconProps = { size: 18, strokeWidth: 2.5, color: '#ffffff' };
  
  const rawType = typeof itemOrType === 'string' 
    ? itemOrType 
    : (itemOrType?.type || itemOrType?.sport_type || itemOrType?.sportType || '');
  
  const isIndoorBike = isIndoorActivity(itemOrType);
  const isOutdoorBike = !isIndoorBike && isCyclingActivity(itemOrType);
  const isRunning = ['Run', 'VirtualRun', 'TrailRun'].includes(rawType) || /carrera|run/i.test(rawType);
  const isGym = ['WeightTraining', 'Workout'].includes(rawType) || /fuerza|pesas|gym/i.test(rawType);
  const isSwimming = ['Swim', 'OpenWaterSwim'].includes(rawType) || /nataci|swim/i.test(rawType);
  const isWalking = ['Walk', 'Hike'].includes(rawType) || /caminata|andar|paseo|senderismo/i.test(rawType);

  let bgColor = 'rgba(255,255,255,0.1)';
  let IconComp = Activity;
  let sportName = rawType || 'Entrenamiento';

  if (isOutdoorBike) {
    bgColor = '#0ea5e9'; // Azul cielo / Exterior
    IconComp = Bike;
    sportName = 'Ciclismo Exterior';
  } else if (isIndoorBike) {
    bgColor = '#d946ef'; // Fucsia Neón / Indoor
    IconComp = IndoorBikeIcon;
    sportName = 'Ciclo Indoor / Rodillo';
  } else if (isRunning) {
    bgColor = '#f97316'; // Naranja
    IconComp = Footprints;
    sportName = 'Carrera';
  } else if (isGym) {
    bgColor = '#a855f7'; // Morado
    IconComp = Dumbbell;
    sportName = 'Fuerza / Gimnasio';
  } else if (isSwimming) {
    bgColor = '#0284c7'; // Azul marino
    IconComp = Waves;
    sportName = 'Natación';
  } else if (isWalking) {
    bgColor = '#10b981'; // Verde esmeralda
    IconComp = Footprints;
    sportName = 'Caminata';
  } else if (isPlanned) {
    bgColor = 'rgba(255,255,255,0.3)';
  }

  const tooltipTitle = isPlanned
    ? `Planificado: ${itemOrType?.title || sportName}`
    : `${itemOrType?.name || sportName}`;

  if (isPlanned) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '32px', height: '32px', borderRadius: '50%',
        border: `2px dashed ${bgColor}`,
        background: 'rgba(255,255,255,0.03)'
      }} title={tooltipTitle}>
        <IconComp {...iconProps} color={bgColor} />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '32px', height: '32px', borderRadius: '50%',
      background: bgColor,
      boxShadow: '0 4px 10px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.2)'
    }} title={tooltipTitle}>
      <IconComp {...iconProps} />
    </div>
  );
};

export const getIntensityInfo = (activity) => {
  if (!activity) return { color: 'transparent', label: 'Sin datos' };
  
  // 1. Usar suffer_score si está disponible
  const s = activity.suffer_score || activity.sufferScore;
  if (s) {
    if (s < 40) return { color: '#22c55e', label: 'Suave / Recuperación (<40 Suffer)' };
    if (s < 80) return { color: '#3b82f6', label: 'Moderado (40-80 Suffer)' };
    if (s < 150) return { color: '#eab308', label: 'Tempo (80-150 Suffer)' };
    if (s < 250) return { color: '#f97316', label: 'Umbral (150-250 Suffer)' };
    return { color: '#ef4444', label: 'Máximo (>250 Suffer)' };
  }
  
  // 2. Usar frecuencia cardíaca si no hay suffer_score
  const hr = activity.average_heartrate || activity.averageHeartrate;
  if (hr) {
    if (hr < 125) return { color: '#22c55e', label: `Suave (<125 ppm)` };
    if (hr < 140) return { color: '#3b82f6', label: `Moderado (${Math.round(hr)} ppm)` };
    if (hr < 155) return { color: '#eab308', label: `Tempo (${Math.round(hr)} ppm)` };
    if (hr < 170) return { color: '#f97316', label: `Umbral (${Math.round(hr)} ppm)` };
    return { color: '#ef4444', label: `Máximo (${Math.round(hr)} ppm)` };
  }
  
  // 3. Fallback basado en tiempo (heurística básica)
  const secs = activity.moving_time || activity.movingTime;
  if (secs) {
    const mins = secs / 60;
    if (mins < 30) return { color: '#22c55e', label: `Suave (<30 min)` };
    if (mins < 60) return { color: '#3b82f6', label: `Moderado (${Math.round(mins)} min)` };
    if (mins < 90) return { color: '#eab308', label: `Tempo (${Math.round(mins)} min)` };
    if (mins < 120) return { color: '#f97316', label: `Umbral (${Math.round(mins)} min)` };
    return { color: '#ef4444', label: `Largo / Intenso (${(mins / 60).toFixed(1)} h)` };
  }
  
  return { color: 'transparent', label: 'Sin datos de intensidad' };
};

const getIntensityColor = (activity) => getIntensityInfo(activity).color;

// Helper para extraer de forma segura e independiente de la zona horaria Año, Mes (0-11) y Día
const getLocalYMD = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    return {
      year: parseInt(parts[0], 10),
      month: parseInt(parts[1], 10) - 1, // 0-indexed
      day: parseInt(parts[2], 10)
    };
  }
  return null;
};

const TrainingCalendar = ({ 
  activities = [], 
  plannedWorkouts = [], 
  appMode = 'athlete',
  athleteId,
  onSavePlanned,
  onDeletePlanned,
  onSaveNotes,
  onCompletePlanned
}) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedPlannedWorkouts, setSelectedPlannedWorkouts] = useState([]);

  const prevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => {
    const next = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);
    // Remove the restriction of not seeing future months so coaches can plan
    setViewDate(next);
  };

  // Group completed activities by day (utilizando start_date_local para evitar desfases de huso horario del visor)
  const actsByDay = {};
  (activities || []).forEach(act => {
    const dateStr = act.start_date_local || act.start_date;
    const ymd = getLocalYMD(dateStr);
    if (ymd && ymd.year === viewDate.getFullYear() && ymd.month === viewDate.getMonth()) {
      const key = ymd.day;
      if (!actsByDay[key]) actsByDay[key] = [];
      actsByDay[key].push(act);
    }
  });

  // Group planned workouts by day (utilizando extracción directa del string para consistencia absoluta)
  const plannedByDay = {};
  (plannedWorkouts || []).forEach(plan => {
    const ymd = getLocalYMD(plan.date);
    if (ymd && ymd.year === viewDate.getFullYear() && ymd.month === viewDate.getMonth()) {
      const key = ymd.day;
      if (!plannedByDay[key]) plannedByDay[key] = [];
      plannedByDay[key].push(plan);
    }
  });

  // Build month cells
  const year = viewDate.getFullYear(), month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isFuture = (d) => new Date(year, month, d) > new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const totalActs = Object.values(actsByDay).flat().length;
  const totalPlanned = Object.values(plannedByDay).flat().length;
  const totalHours = (Object.values(actsByDay).flat().reduce((s, a) => s + a.moving_time, 0) / 3600).toFixed(1);

  const handleCellClick = (d) => {
    if (!d) return;
    const dateObj = new Date(year, month, d);
    const acts = actsByDay[d] || [];
    const planned = plannedByDay[d] || [];

    if (acts.length > 0 || planned.length > 0 || appMode === 'coach') {
      setSelectedDate(dateObj);
      setSelectedActivities(acts);
      setSelectedPlannedWorkouts(planned);
      setModalOpen(true);
    }
  };

  return (
    <>
      <div className="premium-glow-card" style={{ 
        flex: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      background: 'linear-gradient(to right, rgba(15, 23, 42, 0.98) 10%, rgba(15, 23, 42, 0.6)), url("https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?q=80&w=1200&fm=jpg&auto=compress&fit=crop")',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderRadius: '16px', 
      padding: '1.5rem',
      border: '1px solid rgba(255,255,255,0.05)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
    }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 className="text-xl" style={{ margin: 0, fontWeight: 700, color: 'white' }}>Calendario de Entrenos</h2>
            <p className="text-muted text-xs">{totalActs} completados · {totalPlanned} planificados este mes</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px', display: 'flex' }}>
              <ChevronLeft size={18} />
            </button>
            <span style={{ fontSize: '0.9rem', fontWeight: 600, minWidth: '120px', textAlign: 'center' }}>
              {MONTHS_ES[month]} {year}
            </span>
            <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px', display: 'flex' }}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {totalActs === 0 && totalPlanned === 0 && (
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px dashed var(--glass-border)', borderRadius: '8px', padding: '1rem', textAlign: 'center', marginBottom: '0.5rem' }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0 }}>
              {appMode === 'coach' 
                ? 'El calendario está vacío. Pincha en cualquier día del mes para planificarle la primera sesión a tu atleta.' 
                : 'Aún no tienes entrenamientos este mes. ¡Conecta tu reloj o espera a que tu coach planifique tu semana!'}
            </p>
          </div>
        )}

        {/* Días de la semana */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '2px' }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', color: 'var(--text-secondary)', fontWeight: 600, padding: '2px' }}>{d}</div>
          ))}
        </div>

        {/* Celdas del calendario */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
          {cells.map((day, idx) => {
            const acts = day ? (actsByDay[day] || []) : [];
            const planned = day ? (plannedByDay[day] || []) : [];
            
            const hasActs = acts.length > 0;
            const hasPlanned = planned.length > 0 && !hasActs; // Si ya hay completado, no resaltamos el plan como principal
            const hasNotes = acts.some(a => a.trainerNotes); // Check si hay notas del entrenador
            
            const todayCell = day && isToday(day);
            const futureCell = day && isFuture(day);

            const isClickable = day && (hasActs || hasPlanned || appMode === 'coach');

            return (
              <div key={idx} onClick={() => handleCellClick(day)} style={{
                minHeight: '74px', borderRadius: '12px', padding: '6px',
                background: !day ? 'transparent'
                  : todayCell ? 'rgba(252,76,2,0.1)'
                  : hasActs ? 'rgba(255,255,255,0.04)'
                  : hasPlanned ? 'rgba(6, 182, 212, 0.05)' // Cyan background for planned
                  : 'rgba(255,255,255,0.02)',
                border: todayCell ? '1px solid rgba(252,76,2,0.4)' 
                      : hasPlanned ? '1px dashed rgba(6, 182, 212, 0.2)'
                      : hasNotes ? '1px solid var(--health-cyan)'
                      : '1px solid transparent',
                boxShadow: (hasActs && !todayCell) ? 'inset 0 1px 1px rgba(255,255,255,0.03)' : 'none',
                opacity: (futureCell && !hasPlanned) ? 0.4 : 1,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex', flexDirection: 'column', gap: '6px',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={e => {
                if (isClickable && day) {
                  e.currentTarget.style.transform = 'scale(1.04) translateY(-2px)';
                  e.currentTarget.style.background = hasActs ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.06)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.4)';
                  e.currentTarget.style.zIndex = '2';
                }
              }}
              onMouseOut={e => {
                if (isClickable && day) {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.background = todayCell ? 'rgba(252,76,2,0.1)' : hasActs ? 'rgba(255,255,255,0.04)' : hasPlanned ? 'rgba(6, 182, 212, 0.05)' : 'rgba(255,255,255,0.02)';
                  e.currentTarget.style.boxShadow = (hasActs && !todayCell) ? 'inset 0 1px 1px rgba(255,255,255,0.03)' : 'none';
                  e.currentTarget.style.zIndex = '1';
                }
              }}
              >
                {day && (
                  <>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: todayCell ? 700 : 500,
                      color: todayCell ? 'var(--strava-orange)' : 'var(--text-secondary)',
                      paddingLeft: '2px'
                    }}>{day}</span>
                    
                    {/* Indicador de Nota (Feedback) */}
                    {hasNotes && (
                      <div style={{ position: 'absolute', top: '6px', right: '6px', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--health-cyan)', boxShadow: '0 0 6px var(--health-cyan)' }} title="Tiene feedback del entrenador" />
                    )}

                    {/* Íconos de actividades (completadas) */}
                    {hasActs && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginTop: '2px', position: 'relative', zIndex: 1 }}>
                        {acts.slice(0, 3).map((a, i) => (
                          <React.Fragment key={i}>
                            {getSportBadge(a, false)}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {/* Íconos de planes (futuro) */}
                    {hasPlanned && !hasActs && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', opacity: 0.8, justifyContent: 'center', marginTop: '2px', position: 'relative', zIndex: 1 }}>
                        {planned.slice(0, 3).map((p, i) => (
                          <React.Fragment key={i}>
                            {p.isCompleted ? (
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '50%',
                                background: 'rgba(34, 197, 94, 0.15)',
                                border: '1px solid rgba(34, 197, 94, 0.4)'
                              }} title={`Completado: ${p.title || 'Entrenamiento'}`}>
                                <Check size={18} color="#22c55e" />
                              </div>
                            ) : getSportBadge(p, true)}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    
                    {/* Línea Neón de Intensidad en la base */}
                    {hasActs && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        display: 'flex'
                      }}>
                        {acts.map((a, i) => {
                          const info = getIntensityInfo(a);
                          return (
                            <div key={i} 
                              title={`Intensidad: ${info.label}${a.name ? ` (${a.name})` : ''}`}
                              style={{
                                flex: 1,
                                background: info.color,
                                boxShadow: `0 -2px 8px ${info.color}40`,
                                opacity: info.color !== 'transparent' ? 0.9 : 0
                              }} 
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Leyenda Completa */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '0.75rem', 
          marginTop: '1.5rem', 
          paddingTop: '1rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontSize: '0.78rem', 
          color: 'rgba(255,255,255,0.85)'
        }}>
          {/* Fila 1: Estados y Tipos de Ciclismo */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', justifyContent: 'center', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '14px', height: '14px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }}></div> Completado
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '14px', height: '14px', background: 'rgba(6, 182, 212, 0.05)', border: '1px dashed rgba(6, 182, 212, 0.5)', borderRadius: '4px' }}></div> Planificado
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '8px', height: '8px', background: 'var(--health-cyan)', borderRadius: '50%', boxShadow: '0 0 8px var(--health-cyan)' }}></div> Feedback Coach
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bike size={12} color="#ffffff" strokeWidth={2.5} />
              </div> Ciclismo Exterior
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#d946ef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IndoorBikeIcon size={12} color="#ffffff" strokeWidth={2.5} />
              </div> Ciclo Indoor / Rodillo
            </span>
          </div>

          {/* Fila 2: Guía de colores de intensidad (línea inferior de cada día) */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', justifyContent: 'center', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
            <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>Intensidad del día:</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '4px', background: '#22c55e', borderRadius: '2px', display: 'inline-block' }}></span> Suave
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '4px', background: '#3b82f6', borderRadius: '2px', display: 'inline-block' }}></span> Moderado
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '4px', background: '#eab308', borderRadius: '2px', display: 'inline-block' }}></span> Tempo
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '4px', background: '#f97316', borderRadius: '2px', display: 'inline-block' }}></span> Umbral
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '12px', height: '4px', background: '#ef4444', borderRadius: '2px', display: 'inline-block' }}></span> Máximo
            </span>
          </div>
        </div>
      </div>

      <WorkoutModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        selectedDate={selectedDate}
        activitiesDay={selectedActivities}
        plannedWorkoutsDay={selectedPlannedWorkouts}
        appMode={appMode}
        athleteId={athleteId}
        onSavePlanned={onSavePlanned}
        onDeletePlanned={onDeletePlanned}
        onSaveNotes={onSaveNotes}
        onCompletePlanned={onCompletePlanned}
      />
    </>
  );
};

export default TrainingCalendar;
